import { useEffect, useLayoutEffect, useRef, RefObject, useState, useCallback } from "react";

interface UseGestureScrollOptions {
  /** Direction du scroll à capturer: 'horizontal' (deltaX) ou 'vertical' (deltaY) */
  direction: "horizontal" | "vertical";
  /** Nombre total de pages */
  totalPages: number;
  /** Index de la page actuelle */
  currentIndex: number;
  /** Callback appelé pour aller à la page précédente */
  onPrevious: () => void;
  /** Callback appelé pour aller à la page suivante */
  onNext: () => void;
  /** Désactiver le scroll (ex: mode édition) */
  disabled?: boolean;
}

/**
 * Hook pour gérer le scroll par gesture (trackpad/souris) avec pagination.
 * Gère le filtrage du "bounce" trackpad, la détection de nouvelle gesture,
 * et le blocage après navigation.
 *
 * @param ref - Référence vers l'élément DOM sur lequel écouter les événements wheel
 * @param options - Options de configuration du scroll
 */
export function useGestureScroll(
  ref: RefObject<HTMLElement | null>,
  options: UseGestureScrollOptions
): void {
  const {
    direction,
    totalPages,
    currentIndex,
    onPrevious,
    onNext,
    disabled = false,
  } = options;

  // Refs pour le tracking du scroll
  const accumulatedDeltaRef = useRef(0);
  const lastWheelTimeRef = useRef(0);
  const gestureNavigatedRef = useRef(false);
  const lastGestureDirectionRef = useRef(0);
  const lastDeltaRef = useRef(0);

  // Ref pour la valeur stable de currentIndex (éviter stale closures)
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  
  // Store stable refs for callbacks to avoid re-running effect when callbacks change
  const onPreviousRef = useRef(onPrevious);
  const onNextRef = useRef(onNext);
  onPreviousRef.current = onPrevious;
  onNextRef.current = onNext;

  // Track the actual DOM element so the wheel-listener effect re-runs when
  // the ref is attached to a new node (e.g. after AnimatePresence swap).
  // useLayoutEffect fires synchronously after DOM mutations, so we pick up
  // ref changes without polling.
  const [trackedElement, setTrackedElement] = useState<HTMLElement | null>(null);

  const syncElement = useCallback(() => {
    setTrackedElement((prev) => (ref.current !== prev ? ref.current : prev));
  }, [ref]);

  useLayoutEffect(syncElement);
  useEffect(syncElement);

  useEffect(() => {
    const element = trackedElement;
    if (!element) return;

    const handleWheel = (e: WheelEvent) => {
      if (totalPages <= 1 || disabled) return;

      const now = Date.now();

      // Déterminer le delta selon la direction
      let delta: number;
      if (direction === "horizontal") {
        const absX = Math.abs(e.deltaX);
        const absY = Math.abs(e.deltaY);
        const isShiftVerticalScroll = e.shiftKey && absY > 0;
        
        // Ignorer les scrolls purement verticaux en mode horizontal
        // Un scroll est considéré "horizontal" seulement si deltaX est significatif
        // ET deltaX est dominant par rapport à deltaY (ratio > 0.5)
        // Cela permet au scroll vertical de passer pour le contenu scrollable
        if (!isShiftVerticalScroll) {
          const isHorizontalIntent = absX > 2 && (absY === 0 || absX / absY > 0.5);
          if (!isHorizontalIntent) return;
        }
        
        delta = absX > 0 ? e.deltaX : e.deltaY;
      } else {
        delta = e.deltaY;
      }

      const currentDirection = Math.sign(delta);
      if (currentDirection === 0) return;

      // Empêcher le comportement par défaut (navigation browser, scroll parent)
      // On bloque toujours car on gère le wrap-around nous-mêmes
      e.preventDefault();
      e.stopPropagation();

      const timeSinceLastWheel = now - lastWheelTimeRef.current;

      // Wheel events arrivent toutes les ~8-16ms pendant un scroll actif
      // Un gap de 40ms+ indique que l'utilisateur a levé ses doigts (nouvelle gesture)
      const GESTURE_END_THRESHOLD = 40;

      const isNewGestureByTime = timeSinceLastWheel > GESTURE_END_THRESHOLD;

      // Détecter une nouvelle gesture par redémarrage de vélocité :
      // si le dernier delta était très petit (fin de momentum) et le delta actuel est significatif
      const isNewGestureByVelocity =
        Math.abs(lastDeltaRef.current) <= 2 && Math.abs(delta) >= 5;

      // Détecter un changement de direction (l'utilisateur veut aller dans l'autre sens)
      const isDirectionChange =
        lastGestureDirectionRef.current !== 0 &&
        currentDirection !== 0 &&
        currentDirection !== lastGestureDirectionRef.current;

      if (
        isNewGestureByTime ||
        (gestureNavigatedRef.current &&
          (isNewGestureByVelocity || isDirectionChange))
      ) {
        // Nouvelle gesture détectée - reset
        accumulatedDeltaRef.current = 0;
        lastGestureDirectionRef.current = 0;
        gestureNavigatedRef.current = false;
      }

      lastWheelTimeRef.current = now;
      lastDeltaRef.current = delta;

      // Si on a déjà navigué pendant cette gesture, bloquer jusqu'à nouvelle gesture
      if (gestureNavigatedRef.current) return;

      // Tracker la direction pour la gesture courante (défini une seule fois par gesture)
      if (lastGestureDirectionRef.current === 0 && currentDirection !== 0) {
        lastGestureDirectionRef.current = currentDirection;
      }

      // Ignorer les deltas en direction opposée (bounce/recoil du trackpad)
      if (
        lastGestureDirectionRef.current !== 0 &&
        currentDirection !== 0 &&
        currentDirection !== lastGestureDirectionRef.current
      ) {
        return;
      }

      // Accumuler le delta
      accumulatedDeltaRef.current += Math.abs(delta);

      // Déclencher la navigation quand le delta accumulé dépasse le seuil
      const THRESHOLD = 50;
      if (accumulatedDeltaRef.current >= THRESHOLD) {
        // Marquer la gesture comme "navigated" - bloquer jusqu'à nouvelle gesture
        gestureNavigatedRef.current = true;
        accumulatedDeltaRef.current = 0;

        // Call navigation callbacks - let the parent handle wrap-around logic
        if (currentDirection > 0) {
          onNextRef.current();
        } else if (currentDirection < 0) {
          onPreviousRef.current();
        }
      }
    };

    // Ajouter l'event listener avec { passive: false, capture: true }
    // - passive: false permet d'appeler preventDefault()
    // - capture: true intercepte l'événement AVANT qu'il ne soit traité par le navigateur
    //   (nécessaire pour bloquer la navigation back/forward du navigateur)
    element.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    
    return () => {
      element.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, [trackedElement, direction, totalPages, disabled]);
}
